import { formatDate } from '@utils/DateUtils';
import { truncate } from '@utils/HelperFunctions';
import { ChangeEvent, Dispatch, FC, MouseEvent, SetStateAction, useRef } from 'react';
import styled from 'styled-components';
import { RenderPosition } from '../../../../enums/RenderPosition';
import { useRenderPosition } from '../../../../hooks/useRenderPosition';
import { FilterRecallItemType } from '../../../../types/FilterTypes';
import { NumberBadge } from '../../../style';
import RecallDropdownMobile, { RecallBadge } from './RecallDropdownMobile';

type RecallDropdownProps = {
  filteredItems: FilterRecallItemType[];
  searchText: string;
  filterHeight: number;
  setSearchText: Dispatch<SetStateAction<string>>;
  isChecked: (item: FilterRecallItemType) => boolean;
  handleChange: (item: FilterRecallItemType) => void;
  toggleSelection: (selectAll: boolean) => void;
  isMobileDevice?: boolean;
};

const RecallDropdown: FC<RecallDropdownProps> = ({
  filteredItems,
  filterHeight,
  isMobileDevice,
  searchText,
  setSearchText,
  isChecked,
  handleChange,
  toggleSelection,
}) => {
  const recallOptionsRef = useRef<HTMLElement>(null);
  const renderPosition = useRenderPosition(recallOptionsRef);

  const handleSearch = (e: ChangeEvent<HTMLInputElement>): void => {
    setSearchText(e.target.value);
  };

  const reset = (e: MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
    e.preventDefault();
    setSearchText('');
  };

  const onWrapperClick = (e: MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
  };

  if (isMobileDevice) {
    return (
      <RecallWrapper
        ref={recallOptionsRef as any}
        renderPosition={renderPosition}
        filterHeight={filterHeight}
        isMobileDevice={isMobileDevice}
        data-testid='recall-filter-mobile'
        onClick={onWrapperClick}
      >
        <RecallDropdownMobile
          filteredItems={filteredItems}
          searchText={searchText}
          setSearchText={setSearchText}
          isChecked={isChecked}
          handleChange={handleChange}
          toggleSelection={toggleSelection}
          reset={reset}
        />
      </RecallWrapper>
    );
  }

  return (
    <RecallWrapper
      ref={recallOptionsRef as any}
      renderPosition={renderPosition}
      filterHeight={filterHeight}
    >
      <li>
        <div className='container-fluid recall-filter-wrapper'>
          <div className='row'>
            <div className='col-sm-6'>
              <h3>Recalls</h3>
            </div>
            <div className='col-sm-6'>
              <div className='recall-search-container'>
                <span className='recall-search'>
                  <i className='am-icon am-icon-loupe'></i>
                </span>
                <input
                  className='form-control multiselect-search'
                  type='text'
                  placeholder='Search'
                  onChange={handleSearch}
                  value={searchText}
                />
              </div>
            </div>
          </div>
        </div>
        <div className='container-fluid recall-table-wrapper'>
          <div className='row'>
            <div className='col-sm-4 col-sm-offset-8'>
              <span>Recall Date</span>
            </div>
          </div>
          {filteredItems.length > 0 ? (
            <ul className='row recall-menu recall-scrolling-wrapper list-unstyled'>
              {filteredItems.map((item, index) => {
                return (
                  <li key={index}>
                    <div
                      data-testid='recall-date'
                      className='col-sm-8'
                      onClick={() => handleChange(item)}
                    >
                      <span className='checkbox'>
                        <input
                          data-testid='recall-date-check'
                          type='checkbox'
                          className='checkbox'
                          checked={isChecked(item)}
                          onChange={() => handleChange(item)}
                        />
                        {truncate(item.label, 45)}
                      </span>
                    </div>
                    <div className='col-sm-3'>
                      <span className='recall-item-date'>{formatDate(item.date)}</span>
                    </div>
                    <div className='col-sm-1'>
                      <RecallBadge>{item.count}</RecallBadge>
                    </div>
                  </li>
                );
              })}
            </ul>
          ) : (
            <div className='row'>
              <div className='recall-scrolling-wrapper'>
                <p className='reset-link'>
                  No Results
                  <br />
                  <a className='reset-link' onClick={reset}>
                    Click to reset
                  </a>
                </p>
              </div>
            </div>
          )}
          <div className='row select-deselect-wrapper'>
            <p>
              <a onClick={() => toggleSelection(true)}>Select</a> /{' '}
              <a onClick={() => toggleSelection(false)}>Deselect All</a>
              <span
                data-testid='reset-link'
                className={`reset-link ${searchText === '' ? 'disabled' : ''}`}
                onClick={reset}
              >
                <a>Reset</a>
              </span>
            </p>
          </div>
        </div>
      </li>
    </RecallWrapper>
  );
};

export default RecallDropdown;

type RecallWrapperProps = {
  renderPosition?: RenderPosition;
  filterHeight?: number;
} & { isMobileDevice?: boolean };

const RecallWrapper = styled.ul.attrs({
  className: 'multiselect-container dropdown-menu recall-filter',
}) <RecallWrapperProps>`
  ${(props) =>
    props.renderPosition === RenderPosition.Top ? `bottom: ${props.filterHeight}px` : ``};
  ${(props) => (props.renderPosition === RenderPosition.Bottom ? `bottom: auto` : ``)};
    ${(props) =>
    props.isMobileDevice &&
    `
    width: 100%;
    padding: 0;
  `};
`;
